import React, { useState, ReactNode } from 'react';
import { signOut } from 'firebase/auth';
import firebaseAuth, { googleProvider } from './firebase-auth';
import {
    AuthContext,
    AuthContextType,
    AuthLoginPrams,
    AuthLogoutPrams,
} from './use-auth';
import useAuthState from './use-auth-state';
import { appRoutes } from 'app/routes/constants';
import { LoginActionType } from 'app/Pages/LoginPage';

interface FirebaseProviderProps {
    children: ReactNode;
}

const FirebaseProvider: React.FC<FirebaseProviderProps> = ({ children }) => {
    const [error, setError] = useState(null);

    const { user, loading, isAuthenticated } = useAuthState();

    const loginWithRedirect = async (params?: AuthLoginPrams) => {
        try {
            const action: LoginActionType = 'invite';
            window.location.href = `${appRoutes.INVITE_SIGN_UP}?email=${params?.authorizationParams?.email}&action=${action}`;
        } catch (error) {
            console.error('Error logging in:', error.message);
            setError(error);
        }
    };

    const logout = async (params?: AuthLogoutPrams) => {
        try {
            await signOut(firebaseAuth);
            localStorage.clear();
            sessionStorage.clear();
            const { logoutParams } = params;
            if (logoutParams?.navigateTo) {
                window.location.pathname = logoutParams.navigateTo;
            }
            else if (logoutParams?.returnTo) {
                window.location.href = logoutParams.returnTo;
            }
        } catch (error) {
            console.log(error.code);
        }
    };

    const getAccessTokenSilently = async (
        forceRefresh = false,
    ): Promise<string> => {
        if (isAuthenticated) {
            try {
                const idToken =
                    await firebaseAuth.currentUser.getIdToken(forceRefresh);
                return idToken;
            } catch (error) {
                console.error('Failed to get ID token:', error);
                throw error;
            }
        }
        return '';
    };

    const value: AuthContextType = {
        user,
        loading,
        isAuthenticated,
        loginWithRedirect,
        logout,
        getAccessTokenSilently,
        error,
        setError,
    };

    return (
        <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    );
};

export default FirebaseProvider;
